import {
  FastField as FormikFastField,
  Field as FormikField,
  useFormikContext,
} from "formik";
import { get, isNil } from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { isEmptyHtml } from "utils/helper";

import { MenuItem, Select, SelectProps, makeStyles } from "@material-ui/core";
import UnfoldMoreIcon from "@material-ui/icons/ExpandMore";
const useFlagSelectStyles = makeStyles((theme) => ({
  select: {
    borderRadius: 8,
    // border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    transition: "all 0.2s ease-in-out",
    // boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.text.disabled,
    },
    "&:before": {
      display: "none",
    },
    "&:after": {
      display: "none",
    },
    "& .MuiSelect-select": {
      paddingRight: 32,
      paddingLeft: theme.spacing(1.5),
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75),
      display: "flex",
      alignItems: "center",
      "&:focus": {
        backgroundColor: "transparent",
        borderRadius: 8,
      },
      "& img": {
        width: 24,
        height: 16,
        objectFit: "cover",
        borderRadius: 2,
        boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
      },
    },
    "& .MuiSelect-icon": {
      right: 12,
      top: 2,
      color: theme.palette.text.secondary,
      width: 20,
      height: 20,
    },
  },
}));

export const FieldLanguageSelect = ({
  value = "th",
  onChange,
  ...props
}: SelectProps) => {
  const classes = useFlagSelectStyles();

  return (
    <Select
      value={value}
      onChange={onChange}
      className={classes.select}
      IconComponent={UnfoldMoreIcon}
      MenuProps={{
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
        getContentAnchorEl: null,
      }}
      {...props}
    >
      <MenuItem value="th">
        <img
          src="/static/img/flags/th.png"
          alt="TH"
          style={{ width: 27, height: 18 }}
        />
      </MenuItem>
      <MenuItem value="en">
        <img
          src="/static/img/flags/us.png"
          alt="EN"
          style={{ width: 27, height: 18 }}
        />
      </MenuItem>
    </Select>
  );
};

const Field = ({
  fast = true,
  topRightComponent,
  translatable,
  translationPosition = "right",
  ...rest
}: any) => {
  const { i18n } = useTranslation();
  const { options, src, unit, helperText, disabled, onChange } = rest;
  const [language, setLanguage] = React.useState<"th" | "en">(
    (i18n.language || "th").startsWith("en") ? "en" : "th"
  );

  React.useEffect(() => {
    setLanguage((i18n.language || "th").startsWith("en") ? "en" : "th");
  }, [i18n.language]);

  const name = useMemo(() => {
    if (translatable) {
      const suffix = language === "th" ? "Th" : "En";
      return `${rest.name}${suffix}`;
    }
    return rest.name;
  }, [rest.name, translatable, language]);

  const optionsJson = JSON.stringify(options);
  const key = useMemo(() => {
    return uuidv4();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    optionsJson,
    src,
    unit,
    helperText,
    disabled,
    onChange,
    name,
    rest.label,
  ]); // Added rest.label so FastField remounts on language change

  const required = rest.required;
  const componentValidation = rest.component?.validate;
  const validationSchema = rest.component?.validationSchema;

  const formikContext = useFormikContext<any>();
  const valuesRef = React.useRef(formikContext.values);
  const { values, errors, setFieldError, setFieldTouched } = formikContext;

  const checkEmptyValue = (val: any) =>
    isNil(val) || val === "" || (typeof val === "string" && isEmptyHtml(val));

  useEffect(() => {
    valuesRef.current = values;

    if (!translatable) return;

    const thName = `${rest.name}Th`;
    const enName = `${rest.name}En`;
    const hasThValue = !checkEmptyValue(get(values, thName));
    const hasEnValue = !checkEmptyValue(get(values, enName));
    const reqEnError = "client.yup.error.requiredEn";
    const reqThError = "client.yup.error.requiredTh";

    if (hasThValue && !hasEnValue) {
      const enError = get(errors, enName);
      const thError = get(errors, thName);

      if (enError !== reqEnError) {
        setFieldError(enName, reqEnError);
        setFieldTouched(enName, true, false);
      }

      const canSetThError =
        !thError || thError === reqEnError || thError === reqThError;

      if (canSetThError) {
        setFieldError(thName, reqEnError);
        setFieldTouched(thName, true, false);
      }
    } else if (hasEnValue && !hasThValue) {
      const enError = get(errors, enName);
      const thError = get(errors, thName);

      if (thError !== reqThError) {
        setFieldError(thName, reqThError);
        setFieldTouched(thName, true, false);
      }

      const canSetEnError =
        !enError || enError === reqEnError || enError === reqThError;

      if (canSetEnError) {
        setFieldError(enName, reqThError);
        setFieldTouched(enName, true, false);
      }
    } else {
      const currEnError = get(errors, enName);
      const currThError = get(errors, thName);

      if (currEnError === reqEnError || currEnError === reqThError)
        setFieldError(enName, undefined);
      if (currThError === reqEnError || currThError === reqThError)
        setFieldError(thName, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, errors, translatable, rest.name, setFieldError, setFieldTouched]);

  const validate = useCallback(
    (value) => {
      let error: any = undefined;

      if (required && checkEmptyValue(value)) {
        if (translatable) {
          error = name.endsWith("En")
            ? "client.yup.error.requiredEn"
            : "client.yup.error.requiredTh";
        } else {
          error = "client.yup.error.required";
        }
      }

      if (translatable && !error) {
        const isTh = name.endsWith("Th");
        const thName = isTh ? name : `${rest.name}Th`;
        const enName = !isTh ? name : `${rest.name}En`;

        const actualThValue = isTh ? value : get(valuesRef.current, thName);
        const actualEnValue = !isTh ? value : get(valuesRef.current, enName);

        if (!checkEmptyValue(actualThValue) && checkEmptyValue(actualEnValue)) {
          error = "client.yup.error.requiredEn";
        } else if (
          checkEmptyValue(actualThValue) &&
          !checkEmptyValue(actualEnValue)
        ) {
          error = "client.yup.error.requiredTh";
        }
      }

      if (!error && componentValidation) {
        const componentError = componentValidation(value);
        error = componentError == null ? undefined : componentError;
      } else if (!error && validationSchema) {
        try {
          validationSchema.validateSync(value);
        } catch (e: any) {
          error = e.message;
        }
      }

      return error == null ? undefined : error;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      required,
      componentValidation,
      validationSchema,
      translatable,
      name,
      rest.name,
    ]
  );

  useEffect(() => {
    if (!translatable) return;

    const isTh = language === "th";
    const currentName = `${rest.name}${isTh ? "Th" : "En"}`;
    const otherName = `${rest.name}${isTh ? "En" : "Th"}`;
    const otherError = get(formikContext.errors, otherName);

    if (otherError && typeof otherError === "string") {
      formikContext.setFieldError(otherName, otherError);
    }

    const eitherTouched =
      get(formikContext.touched, currentName) ||
      get(formikContext.touched, otherName) ||
      formikContext.submitCount > 0;

    if (eitherTouched) {
      formikContext.setFieldTouched(currentName, true, false);
    }
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldProps = { ...rest, name, translatable };

  const fieldComponent = fast ? (
    <FormikFastField key={key} validate={validate} {...fieldProps} />
  ) : (
    <FormikField validate={validate} {...fieldProps} />
  );

  let finalTopRightComponent = topRightComponent;
  if (translatable && !finalTopRightComponent) {
    finalTopRightComponent = (
      <FieldLanguageSelect
        value={language}
        onChange={(e: any) => setLanguage(e.target.value)}
      />
    );
  }

  if (finalTopRightComponent) {
    let style: any = {
      position: "absolute",
      zIndex: 1,
    };
    switch (translationPosition) {
      case "top-right":
        style = {
          ...style,
          right: "1%",
          top: "28px",
        };
        break;
      case "right":
        style = {
          ...style,
          right: "1%",
          top: "14px",
        };
        break;
      case "notification-style":
        style = {
          ...style,
          right: "1%",
          top: "10px",
        };
        break;
      case "fatca-question":
        style = {
          ...style,
          right: "2%",
          top: "8px",
        };
        break;
      case "multiline-labeled":
        style = {
          ...style,
          right: "1%",
          top: "80px",
        };
        break;
      default:
        break;
    }
    return (
      <div
        style={{
          position: "relative",
          width: rest.fullWidth ? "100%" : "auto",
        }}
      >
        <div style={style}>{finalTopRightComponent}</div>
        {fieldComponent}
      </div>
    );
  }

  return fieldComponent;
};

export default Field;
